import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import {
  INN_ERROR,
  MAX_CHARACTERS_ERROR,
  OGRN_LENGTH_ERROR,
  REQUIRED_ERROR,
  START_DATE_NO_LATER_STOP_DATE_ERROR,
  STOP_DATE_NO_SOONER_START_DATE_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { KnmFormValidator } from 'KnmPackage/MainModule/types/KnmFormValidator';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeMaxNumber } from 'CorePackage/MainModule/validators/maxNumber';
import { makeIsNormalFloat } from 'CorePackage/MainModule/validators/isNormalFloat';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnmErpFormChecklist } from 'KnmErpPackage/KnmErpChecklistsModule/interfaces/IKnmErpFormChecklist';
import { IKnmErpFormReason } from 'KnmErpPackage/KnmErpApproveInfoModule/interfaces/IKnmErpFormReason';
import { IKnmErpFormRejectReason } from 'KnmErpPackage/KnmErpApproveInfoModule/interfaces/IKnmErpFormRejectReason';
import { IKnmErpFormCollaboratingOrganization } from 'KnmErpPackage/KnmErpKnoOrganizationModule/interfaces/IKnmErpFormCollaboratingOrganization';
import { makeMatchSelectValueValidation } from 'CorePackage/MainModule/validators/isMatch';
import { makeIsInn } from 'CorePackage/MainModule/validators/isInn';
import { makeIsOgrn } from 'CorePackage/MainModule/validators/isOgrn';
import { makeAssignmentNumberValidation } from 'CorePackage/MainModule/validators/isAssignmentNumber';
import { IKnmErpFormObject } from 'KnmErpPackage/KnmErpSubjectModule/interfaces/IKnmErpFormObject';
import { IKnmErpFormResult } from 'KnmErpPackage/KnmErpResultsModule/interfaces/IKnmErpFormResult';
import { DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmErpFormViolation } from 'KnmErpPackage/KnmErpViolationsModule/interfaces/IKnmErpFormViolation';
import { IKnmErpFormViolationsLawsuit } from 'KnmErpPackage/KnmErpLawsuitsModule/interfaces/IKnmErpFormViolationsLawsuit';
import { IKnmErpFormResultCancellationInfo } from 'KnmErpPackage/KnmErpCancellationInfoModule/interfaces/IKnmErpFormResultCancellationInfo';
import { IKnmFormDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmFormDocument';
import { IKnmErpFormEvent } from 'KnmErpPackage/KnmErpChecklistsModule/interfaces/IKnmErpFormEvent';
import { InspectionDecisionsEnum } from 'KnmPackage/MainModule/enums/InspectionDecisionsEnum';
import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { isOneReasonLinkRequired } from 'KnmPackage/MainModule/common/isOneReasonLinkRequired';
import { IKnmErpErvkFormObject } from 'KnmErpPackage/KnmErpSubjectModule/interfaces/IKnmErpErvkFormObject';
import { NOT_IN_FIAS_TEXT } from 'CorePackage/MainModule/constants/notInFIASText';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { getArrayError } from '../../../CorePackage/MainModule/common/getArrayError';
import { IKnmErpFormErvkReason } from '../../KnmErpApproveInfoModule/interfaces/IKnmErpFormErvkReason';
import { getRiskIndicatorsValidate } from '../../../KnmPackage/MainModule/common/getRiskIndicatorsValidate';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { store } from '../../../CorePackage/MainModule/common/store';

export const knmErpValidator = ({
  values,
  settings,
}: KnmFormValidator & { values: IKnmForm }): FormErrors<IKnmForm> => {
  const {
    subjectTypeErvk,
    subjectType,
    kind,
    kindErvk,
    type,
    typeErvk,
    month,
    orderNumber,
    orderDate,
    headOrderNumber,
    stopDate,
    startDate,
    legalBases,
    inspectionTarget,
    durationHours,
    durationDays,
    checklists,
    prosecutorOrganization,
    reasons,
    approveOrderNumber,
    rejectReasons,
    signerFullName,
    signerPosition,
    knoOrganization,
    knoOrganizationErvk,
    federalLaw,
    reasonsErvk,
    inspectors,
    requirements,
    inspectorsErvk,
    supervisionType,
    supervisionTypeErvk,
    collaboratingKnoName,
    collaboratingOrganizations,
    organizationName,
    inn,
    ogrn,
    objects,
    objectsErvk,
    auditedPersonAuthority,
    decisionPlace,
    objectsResults,
    violations,
    violationsLawsuits,
    resultsCancellationInfo,
    documents,
    events,
    inspectionDecision,
    created,
  } = values;
  const isErvkParamStart = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ERVK_PARAM_TABLES_4_1_23_STARTS_FROM,
  );
  const typeCode = isErvkParamStart ? typeErvk?.code : type?.code;

  const state = store.getState();
  const isProsecutorOrSuperProsecutor = getUserRoleIsProsecutorOrSuperProsecutor(state);

  const isUse4136 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM);

  const getOrderNumberValidate = () => {
    const maxCharactersValidator = makeMaxCharactersValidator(128, MAX_CHARACTERS_ERROR);

    if (typeCode === KnmTypeCodesEnum.VP) {
      return composeValidators(maxCharactersValidator, makeRequired(REQUIRED_ERROR))(orderNumber);
    }

    return maxCharactersValidator(orderNumber || '');
  };

  const getOrderDateValidate = () => {
    if (typeCode === KnmTypeCodesEnum.VP) {
      return makeRequired(REQUIRED_ERROR)(orderDate);
    }
  };

  const getHeadOrderNumberValidate = () => {
    if (headOrderNumber) {
      return makeMaxCharactersValidator(128, MAX_CHARACTERS_ERROR)(headOrderNumber);
    }
  };
  const getStarDateValidate = () => {
    const getStopDateValue = () => stopDate;

    return composeValidators(
      makeNoLaterThenValidator(START_DATE_NO_LATER_STOP_DATE_ERROR, getStopDateValue),
      makeRequired(REQUIRED_ERROR),
    )(startDate);
  };

  const validateObject = (
    item: IKnmErpFormObject | IKnmErpErvkFormObject,
  ): {
    address: string | undefined;
    manualAddress: string | undefined;
    addressType: string | undefined;
    objectType: string | undefined;
    supervisions: {}[] | undefined;
  } => ({
    address: !item.manualAddress
      ? composeValidators(
          isUse4136 && item.isErrorFiasValue && !item.notInFIAS ? () => NOT_IN_FIAS_TEXT : () => undefined,
          makeRequired('Поле не может быть пустым'),
          makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
        )(item.address)
      : () => undefined,
    manualAddress: !item.address
      ? composeValidators(
          makeRequired('Поле не может быть пустым'),
          makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
        )(item.manualAddress)
      : () => undefined,
    addressType:
      !isUse4136 && 'addressType' in item
        ? composeValidators(makeRequired('Поле не может быть пустым'))(item.addressType)
        : () => undefined,
    objectType: isErvkParamStart
      ? composeValidators(
          federalLaw?.number !== '414' && federalLaw?.number !== '131'
            ? makeRequired('Поле не может быть пустым')
            : () => undefined,
        )(item.objectType)
      : makeRequired('Поле не может быть пустым')(item.objectType),
    supervisions:
      item.supervisions?.map(supervision => makeRequired('Поле не может быть пустым')(supervision) || {}) || undefined,
  });

  const getStopDateValidate = () => {
    const getStartDateValue = () => startDate;
    const noSoonerThenValidator = makeNoSoonerThenValidator(STOP_DATE_NO_SOONER_START_DATE_ERROR, getStartDateValue);

    if (typeCode === KnmTypeCodesEnum.VP) {
      return composeValidators(noSoonerThenValidator, makeRequired(REQUIRED_ERROR))(stopDate);
    }
    if (stopDate) {
      return noSoonerThenValidator(stopDate);
    }
  };

  const getRequiredArrayValidate = (value: unknown, typeCodeArg?: string) => {
    if (
      (typeCodeArg ? typeCodeArg === KnmTypeCodesEnum.VP : true) &&
      ((Array.isArray(value) && !value?.length) || value === undefined)
    ) {
      const newArr: { [index: string]: string } | undefined = { 'FINAL_FORM/array-error': REQUIRED_ERROR };

      return newArr;
    }
  };

  const getDurationDaysValidate = () => {
    const normalIntegerValidator = makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR);
    const maxNumberValidator = makeMaxNumber(365, 'Число не может превышать 365');

    if (!durationHours) {
      return composeValidators(makeRequired(REQUIRED_ERROR), normalIntegerValidator, maxNumberValidator)(durationDays);
    }

    return composeValidators(normalIntegerValidator, maxNumberValidator)(durationDays);
  };

  const getDurationHoursValidate = () => {
    const normalFloatValidator = makeIsNormalFloat(UNALLOWED_CHARACTERS_ERROR);
    const maxNumberValidator = makeMaxNumber(256, 'Число не может превышать 256');

    if (!durationDays) {
      return composeValidators(makeRequired(REQUIRED_ERROR), normalFloatValidator, maxNumberValidator)(durationHours);
    }

    return composeValidators(normalFloatValidator, maxNumberValidator)(durationHours);
  };

  const getChecklistsValidate = (): FormErrors<IKnmErpFormChecklist>[] | undefined => {
    return checklists?.map(item => ({
      name: makeRequired('Поле не может быть пустым')(item.name),
      object: makeRequired('Поле не может быть пустым')(item.object),
    }));
  };

  const getReasonsValidate = (): FormErrors<IKnmErpFormReason>[] | undefined => {
    const isKnmReasons411Fields = isLaterThanAdminSetting(
      created,
      settings,
      AdminSettingCodesEnum.KNM_VALIDATION_4_1_1_STARTS_FROM,
    );

    return reasons?.map(item => ({
      type: makeRequired(REQUIRED_ERROR)(item.type),
      text: item.type?.hasText ? makeRequired(REQUIRED_ERROR)(item.text) : undefined,
      date: item.type?.dateRequired ? makeRequired(REQUIRED_ERROR)(item.date) : undefined,
      assignmentNumber:
        isKnmReasons411Fields && item.type?.assignmentNumberRequired
          ? composeValidators(
              makeRequired(REQUIRED_ERROR),
              item.type?.flkRequired
                ? makeAssignmentNumberValidation('Номер поручения указан неверно')
                : () => undefined,
            )(item.assignmentNumber)
          : undefined,
      assignmentDate:
        isKnmReasons411Fields && item.type?.assignmentDateRequired
          ? makeRequired(REQUIRED_ERROR)(item.assignmentDate)
          : undefined,
      requirementDetails:
        isKnmReasons411Fields && (item.type?.requirementDetailsRequired || item.type?.detailsRequirement)
          ? makeRequired(REQUIRED_ERROR)(item.requirementDetails)
          : undefined,
      dateRequirement: item.type?.dateRequirement ? makeRequired(REQUIRED_ERROR)(item.dateRequirement) : undefined,
    }));
  };

  const getReasonsValidateErvk = (): FormErrors<IKnmErpFormErvkReason>[] | undefined => {
    return reasonsErvk?.map(item => {
      const getOrderValidate = (value: unknown) =>
        isAssignmentRequiredByType(item.type) ? makeRequired('Поле не может быть пустым')(value) : undefined;

      return {
        type: makeRequired('Поле не может быть пустым')(item.type),
        text:
          !isProsecutorOrSuperProsecutor && item.type?.hasText
            ? makeRequired('Поле не может быть пустым')(item.text)
            : undefined,
        date:
          !isProsecutorOrSuperProsecutor &&
          !item.type?.assignmentNumberRequired &&
          !item.type?.approveRequired &&
          item.type?.dateRequired
            ? makeRequired('Поле не может быть пустым')(item.date)
            : undefined,
        riskIndicators: item.type?.indicatorRiskRequired
          ? getRiskIndicatorsValidate(item.riskIndicators, item.type?.indicatorRiskRequiredFile)
          : undefined,
        order: getOrderValidate(item.order),
        assignmentNumber: item.order ? makeRequired(REQUIRED_ERROR)(item.assignmentNumber) : undefined,
        assignmentDate: item.order ? makeRequired(REQUIRED_ERROR)(item.assignmentDate) : undefined,
        fullName: item.order ? makeRequired(REQUIRED_ERROR)(item.fullName) : undefined,
        requirementDetails:
          !isProsecutorOrSuperProsecutor && (item.type?.requirementDetailsRequired || item.type?.detailsRequirement)
            ? makeRequired('Поле не может быть пустым')(item.requirementDetails)
            : undefined,
        dateRequirement:
          !isProsecutorOrSuperProsecutor && item.type?.dateRequirement
            ? makeRequired('Поле не может быть пустым')(item.dateRequirement)
            : undefined,
        links: isOneReasonLinkRequired(item.type) ? makeRequired(REQUIRED_ERROR)(item.links) : undefined,
        linkValidation: isOneReasonLinkRequired(item.type)
          ? composeValidators(
              item.links?.some(link => link.linkValidationMessage)
                ? () => item.links?.find(link => link.linkValidationMessage)?.linkValidationMessage
                : () => undefined,
            )(item.links)
          : undefined,
        lastConductedKnmDate:
          !isProsecutorOrSuperProsecutor && item.type?.lastConductedKnmDateRequired
            ? makeRequired('Поле не может быть пустым')(item.lastConductedKnmDate)
            : undefined,
        programNumber:
          !isProsecutorOrSuperProsecutor &&
          item?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM &&
          item.type?.programNumberRequired
            ? makeRequired('Поле не может быть пустым')(item.programNumber)
            : undefined,
        programAttachment:
          !isProsecutorOrSuperProsecutor &&
          item?.type?.name === KnmReasonTypeNameEnum.CHECKING_PROGRAM &&
          item.type?.programFileRequired
            ? makeRequired('Поле не может быть пустым')(item.programAttachment)
            : undefined,
      };
    });
  };

  const getRejectReasonsValidate = (): FormErrors<IKnmErpFormRejectReason>[] | undefined => {
    return rejectReasons?.map(item => ({
      type: makeRequired(REQUIRED_ERROR)(item.type),
      text: item.type?.hasText ? makeRequired(REQUIRED_ERROR)(item.text) : undefined,
    }));
  };

  const getCollaboratingOrganizationsValidate = (): FormErrors<IKnmErpFormCollaboratingOrganization>[] | undefined => {
    const getKnoOrganizationValue = () => knoOrganization?.value;

    return collaboratingOrganizations?.map(item => ({
      organization: composeValidators(
        makeRequired('Поле не может быть пустым'),
        makeMatchSelectValueValidation(
          'Значение поля совпадает со значением поля Наименование органа контроля',
          getKnoOrganizationValue,
        ),
      )(item.organization),
    }));
  };

  const getObjectsResultsValidate = (): FormErrors<IKnmErpFormResult>[] | undefined => {
    const getStarDateValue = () => startDate;

    return objectsResults?.map(item => ({
      object: makeRequired('Поле не может быть пустым')(item.object),
      actAddress: !item.manualAddress
        ? composeValidators(
            isUse4136 && item.isErrorFiasValue && !item.notInFIAS && !item.isWrongAddress
              ? () => NOT_IN_FIAS_TEXT
              : () => undefined,
            isUse4136 && item.isWrongAddress ? () => 'Необходимо ввести корректный адрес' : () => undefined,
            makeRequired('Поле не может быть пустым'),
            makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
          )(item.actAddress)
        : () => undefined,
      manualAddress: !item.actAddress
        ? composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки'),
          )(item.manualAddress)
        : () => undefined,
      actCreateDate: composeValidators(
        makeRequired('Поле не может быть пустым'),
        makeNoSoonerThenValidator(
          'Дата и время составления акта не должны быть раньше даты начала проведения КНМ',
          getStarDateValue,
          DATE_FORMAT_LONG_MINUTE_FNS,
        ),
      )(item.actCreateDate),
      actAddressType: !isUse4136
        ? composeValidators(makeRequired('Поле не может быть пустым'))(item.actAddressType)
        : () => undefined,
      startDate: composeValidators(
        makeRequired('Поле не может быть пустым'),
        makeNoSoonerThenValidator(
          'Дата и время проведения КНМ не должны быть раньше даты начала проведения КНМ',
          getStarDateValue,
          DATE_FORMAT_LONG_MINUTE_FNS,
        ),
      )(item.startDate),
      durationDays: composeValidators(
        makeIsNormalInteger('Введены недопустимые символы'),
        makeMaxNumber(256, 'Число не может превышать 256'),
      )(item.durationDays),
      durationHours: composeValidators(
        makeIsNormalFloat('Введены недопустимые символы'),
        makeMaxNumber(365, 'Число не может превышать 365'),
      )(item.durationHours),
      inspectors: item.inspectors?.map(inspector => ({
        fullName: composeValidators(
          makeRequired('Поле не может быть пустым'),
          makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
        )(inspector?.fullName),
        position: makeRequired('Поле не может быть пустым')(inspector?.position),
        type: makeRequired('Поле не может быть пустым')(inspector?.type),
      })),
      delegates: item.delegates?.map(delegat => ({
        fullName: makeRequired('Поле не может быть пустым')(delegat?.fullName),
        position: makeRequired('Поле не может быть пустым')(delegat?.position),
        type: makeRequired('Поле не может быть пустым')(delegat?.type),
      })),
      additionalInfo: item.additionalInfo?.map(additionalInfo => ({
        type: makeRequired('Поле не может быть пустым')(additionalInfo?.type),
        information: makeRequired('Поле не может быть пустым')(additionalInfo?.information),
      })),
    }));
  };

  const getViolationsValidate = (): FormErrors<IKnmErpFormViolation>[] | undefined => {
    return violations?.map(item => ({
      result: makeRequired('Поле не может быть пустым')(item.result),
      type: makeRequired('Поле не может быть пустым')(item.type),
      note: makeRequired('Поле не может быть пустым')(item.note),
    }));
  };

  const getViolationsLawsuitsValidate = (): FormErrors<IKnmErpFormViolationsLawsuit>[] | undefined => {
    return violationsLawsuits?.map(item => ({
      violation: makeRequired('Поле не может быть пустым')(item.violation),
      lawsuits: item.lawsuits?.map(lawsuit => ({
        lawsuitType: makeRequired('Поле не может быть пустым')(lawsuit.lawsuitType),
        text: makeRequired('Поле не может быть пустым')(lawsuit.text),
      })),
    }));
  };

  const getResultsCancellationInfoValidate = (): FormErrors<IKnmErpFormResultCancellationInfo>[] | undefined => {
    return resultsCancellationInfo?.map(item => ({
      result: makeRequired('Поле не может быть пустым')(item.result),
      information: makeRequired('Поле не может быть пустым')(item.information),
    }));
  };

  const getDocumentsValidate = (): FormErrors<IKnmFormDocument>[] | undefined => {
    return documents?.map(item => ({
      type: makeRequired('Поле не может быть пустым')(item?.type),
    }));
  };

  const getEventsValidate = (): FormErrors<IKnmErpFormEvent>[] | undefined => {
    const getStartDateValue = () => startDate;

    return events?.map(item => ({
      name: composeValidators(
        makeRequired(REQUIRED_ERROR),
        makeMaxCharactersValidator(2048, MAX_CHARACTERS_ERROR),
      )(item?.name),
      startDate: composeValidators(
        makeNoSoonerThenValidator(
          'Дата начала мероприятия не должна быть раньше даты начала проведения КНМ',
          getStartDateValue,
        ),
        makeNoLaterThenValidator(
          'Дата начала мероприятия не должна быть позже даты окончания мероприятия',
          () => item?.stopDate,
        ),
      )(item?.startDate),
      stopDate: item?.stopDate
        ? makeNoSoonerThenValidator(
            'Дата окончания мероприятия не должна быть раньше даты начала мероприятия',
            () => item?.startDate,
          )(item?.stopDate)
        : undefined,
    }));
  };

  const federalLawValidator = isErvkParamStart
    ? { federalLaw: makeRequired('Поле не может быть пустым')(federalLaw) }
    : {};

  const reasonsValidator = isErvkParamStart
    ? ({
        reasonsErvk: reasonsErvk?.length ? getReasonsValidateErvk() : 'Поле не может быть пустым',
      } as const)
    : ({
        reasons: reasons?.length ? getReasonsValidate() : getRequiredArrayValidate(reasons),
      } as const);

  const objectsValidator = isErvkParamStart
    ? {
        objectsErvk: !isProsecutorOrSuperProsecutor ? objectsErvk?.map(validateObject) : undefined,
      }
    : {
        objects: objects?.map(validateObject),
      };

  const superVisionTypeValidator = isErvkParamStart
    ? { supervisionTypeErvk: makeRequired(REQUIRED_ERROR)(supervisionTypeErvk) }
    : { supervisionType: makeRequired('Поле не может быть пустым')(supervisionType) };

  const knoOrganisationsValidator = isErvkParamStart
    ? { knoOrganizationErvk: makeRequired(REQUIRED_ERROR)(knoOrganizationErvk) }
    : { knoOrganization: makeRequired('Поле не может быть пустым')(knoOrganization) };

  const subjectTypeValidator = isErvkParamStart
    ? {
        subjectTypeErvk: makeRequired('Поле не может быть пустым')(subjectTypeErvk),
      }
    : {
        subjectType: makeRequired('Поле не может быть пустым')(subjectType),
      };

  const inspectorsValidator = isErvkParamStart
    ? {
        inspectorsErvk: inspectorsErvk?.map(item => ({
          fullName: makeRequired('Поле не может быть пустым')(item.fullName),
          position: makeRequired('Поле не может быть пустым')(item.position),
        })),
      }
    : {
        inspectors: inspectors?.map(item => ({
          fullName: makeRequired('Поле не может быть пустым')(item.fullName),
          position: composeValidators(
            makeRequired('Поле не может быть пустым'),
            makeMaxCharactersValidator(256, 'Превышена допустимая длина строки'),
          )(item.position),
          type: makeRequired('Поле не может быть пустым')(item.type),
        })),
      };

  const typeValidator = isErvkParamStart
    ? {
        typeErvk: makeRequired('Поле не может быть пустым')(typeErvk),
      }
    : {
        type: makeRequired('Поле не может быть пустым')(type),
      };

  const isErpKindValidation = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.ERP_KIND_VALIDATION);

  const getKindValidator = () => {
    if (isErvkParamStart) {
      return { kindErvk: makeRequired('Поле не может быть пустым')(kindErvk) };
    }

    return isErpKindValidation ? { kind: makeRequired('Поле не может быть пустым')(kind) } : {};
  };

  const kindValidator = getKindValidator();

  const auditedPersonAuthorityValidator =
    federalLaw?.number === '68'
      ? {
          auditedPersonAuthority: auditedPersonAuthority?.length
            ? auditedPersonAuthority.map(item => ({
                authority: makeRequired('Поле не может быть пустым')(item.authority),
              }))
            : getRequiredArrayValidate(auditedPersonAuthority),
        }
      : {};

  const resultValidateObj: FormErrors<IKnmForm> = {
    ...subjectTypeValidator,
    ...typeValidator,
    ...kindValidator,
    month: typeCode === KnmTypeCodesEnum.PP ? makeRequired('Поле не может быть пустым')(month) : undefined,
    orderNumber: getOrderNumberValidate(),
    orderDate: getOrderDateValidate(),
    headOrderNumber: getHeadOrderNumberValidate(),
    startDate: getStarDateValidate(),
    stopDate: getStopDateValidate(),
    ...auditedPersonAuthorityValidator,
    legalBases: getRequiredArrayValidate(legalBases, typeCode),
    requirements: getRequiredArrayValidate(requirements, typeCode),
    inspectionTarget: composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeMaxCharactersValidator(2048, MAX_CHARACTERS_ERROR),
    )(inspectionTarget),
    durationDays: getDurationDaysValidate(),
    durationHours: getDurationHoursValidate(),
    checklists: getChecklistsValidate(),
    prosecutorOrganization: makeRequired('Поле не может быть пустым')(prosecutorOrganization),
    ...reasonsValidator,
    approveOrderNumber: makeMaxCharactersValidator(128, 'Превышена допустимая длина строки')(approveOrderNumber || ''),
    rejectReasons:
      (inspectionDecision?.id === InspectionDecisionsEnum.DECLINED &&
        (rejectReasons?.length ? getRejectReasonsValidate() : getRequiredArrayValidate(rejectReasons))) ||
      undefined,
    signerFullName: makeMaxCharactersValidator(256, 'Превышена допустимая длина строки')(signerFullName || ''),
    signerPosition: makeMaxCharactersValidator(256, 'Превышена допустимая длина строки')(signerPosition || ''),
    ...knoOrganisationsValidator,
    ...federalLawValidator,
    ...inspectorsValidator,
    ...superVisionTypeValidator,
    collaboratingKnoName: makeMaxCharactersValidator(
      2048,
      'Превышена допустимая длина строки',
    )(collaboratingKnoName || ''),
    collaboratingOrganizations: getCollaboratingOrganizationsValidate(),
    organizationName: composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
    )(organizationName),
    inn: composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
      makeIsInn(INN_ERROR()),
    )(inn),
    ogrn: composeValidators(makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR), makeIsOgrn(OGRN_LENGTH_ERROR))(ogrn),
    decisionPlace: makeMaxCharactersValidator(2048, 'Превышена допустимая длина строки')(decisionPlace || ''),
    ...objectsValidator,
    objectsResults: getObjectsResultsValidate(),
    violations: getViolationsValidate(),
    violationsLawsuits: getViolationsLawsuitsValidate(),
    resultsCancellationInfo: getResultsCancellationInfoValidate(),
    documents: getDocumentsValidate(),
    events: events?.length ? getEventsValidate() : getRequiredArrayValidate(events, typeCode),
  };

  const resultValidate = JSON.parse(JSON.stringify(resultValidateObj));

  if (typeof resultValidate['reasonsErvk' as keyof IKnmForm] === 'string') {
    resultValidate['reasonsErvk' as keyof IKnmForm] = getArrayError(resultValidate['reasonsErvk' as keyof IKnmForm]);
  }

  if (Array.isArray(resultValidate['reasonsErvk' as keyof IKnmForm])) {
    resultValidate['reasonsErvk' as keyof IKnmForm].forEach(
      (reasonItem: { [key: string]: string | object | undefined }, index: number) => {
        const riskIndicators = reasonItem['riskIndicators' as keyof IKnmForm];

        if (reasonItem && typeof riskIndicators === 'string') {
          resultValidate['reasonsErvk' as keyof IKnmForm][index]['riskIndicators' as keyof IKnmForm] = getArrayError(
            riskIndicators,
          );
        }
      },
    );
  }

  return resultValidate;
};
